<template>
	<v-row dense data-tour="kpi">
		<v-col v-for="t in tiles" :key="t.key" cols="6" sm="4" lg="2">
			<v-card outlined class="ak-tile pa-3" height="100%">
				<div class="text-caption grey--text text--darken-1 text-uppercase">
					{{ t.label }}
					<v-tooltip bottom max-width="320">
						<template v-slot:activator="{ on, attrs }">
							<v-icon x-small v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
						</template>
						<span>{{ t.help }}</span>
					</v-tooltip>
				</div>
				<div class="ak-value" :class="t.valueClass">{{ t.value }}</div>
				<div class="d-flex align-center" style="gap: 6px">
					<v-chip v-if="t.delta !== undefined" x-small label :color="t.color" text-color="white">{{ t.delta }}</v-chip>
					<span class="text-caption grey--text text-truncate">{{ t.hint }}</span>
				</div>
			</v-card>
		</v-col>
	</v-row>
</template>

<script>
import { formatEur, formatEurCents, formatNumber, formatDelta, deltaColor } from '@/components/dashboard/format';
import { formatRoas } from './labels';

const delta = (a, b) => {
	if (a === null || b === null || a === undefined || b === undefined) return null;
	if (!b) return a ? null : 0;
	return Math.round(((a - b) / Math.abs(b)) * 1000) / 10;
};

export default {
	name: 'AdsKpis',
	props: {
		report: { type: Object, required: true },
	},
	computed: {
		tiles() {
			const c = this.report.current;
			const p = this.report.previous;
			const s = this.report.sales;
			const tile = (key, label, value, hint, help, d, inverse = false, extra = {}) => ({
				key,
				label,
				value,
				hint,
				help,
				...(d === undefined ? {} : { delta: formatDelta(d), color: deltaColor(d, inverse) }),
				...extra,
			});
			const below = c.roas !== null && c.breakevenRoas !== null && c.roas < c.breakevenRoas;
			return [
				tile('spend', 'Spesa', formatEur(c.spend), `prima ${formatEur(p.spend)}`, 'Spesa di tutte le piattaforme collegate nel periodo, IVA esclusa.', delta(c.spend, p.spend), true),
				tile(
					'roas',
					'ROAS complessivo',
					formatRoas(c.roas),
					`${formatEur(s.revenue)} di vendite`,
					'Vendite di tutti i canali (come nella Home) divise per la spesa. È il numero affidabile: non dipende da come le piattaforme si attribuiscono le vendite.',
					delta(c.roas, p.roas),
					false,
					{ valueClass: below ? 'red--text text--darken-1' : '' },
				),
				tile(
					'platformRoas',
					'ROAS dichiarato',
					formatRoas(c.platformRoas),
					`${formatEur(c.conversionValue)} secondo le piattaforme`,
					'Valore delle conversioni secondo Google, Meta… diviso per la spesa. Ogni piattaforma si attribuisce anche vendite contate dalle altre: la somma può superare le vendite vere.',
					delta(c.platformRoas, p.platformRoas),
				),
				tile(
					'cac',
					'Costo per nuovo cliente',
					c.costPerNewCustomer === null ? '—' : formatEurCents(c.costPerNewCustomer),
					`${formatNumber(s.newCustomers)} nuovi clienti`,
					'Spesa divisa per i clienti al primo ordine nel periodo (stessa regola della Home).',
					delta(c.costPerNewCustomer, p.costPerNewCustomer),
					true,
				),
				tile(
					'breakeven',
					'ROAS di pareggio',
					formatRoas(c.breakevenRoas),
					c.breakevenRoas === null ? 'costi d’acquisto mancanti' : below ? 'il ROAS è sotto: in perdita' : 'il ROAS è sopra: in utile',
					'Il ROAS minimo perché la pubblicità non si mangi tutto il margine lordo: vendite divise per il margine lordo del periodo.',
					undefined,
					false,
					{ valueClass: below ? 'red--text text--darken-1' : '' },
				),
				tile(
					'margin',
					'Margine dopo la pubblicità',
					c.marginAfterAds === null ? 'n.d.' : formatEur(c.marginAfterAds),
					c.marginAfterAds === null ? 'costi d’acquisto mancanti' : `prima ${p.marginAfterAds === null ? 'n.d.' : formatEur(p.marginAfterAds)}`,
					'Margine lordo dei prodotti (come nella Home) meno la spesa pubblicitaria. Commissioni e spese generali sono nel report finanziario.',
					c.marginAfterAds === null ? undefined : delta(c.marginAfterAds, p.marginAfterAds),
				),
			];
		},
	},
};
</script>

<style scoped>
.ak-value {
	font-size: 1.5rem;
	font-weight: 600;
	line-height: 1.3;
	margin: 2px 0 4px;
	white-space: nowrap;
}
</style>
